import { useState, useRef, useEffect } from "react";
import {
  IoChevronDownOutline,
  IoChevronBack,
  IoChevronForward,
} from "react-icons/io5";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

const MAX_VISIBLE_TABS = 6; // Show up to 6 tabs before "More"

const TabComponent = ({ tabs, activeTab, setActiveTab }) => {
  const visibleTabs = tabs.slice(0, MAX_VISIBLE_TABS); // First 6 tabs
  const extraTabs = tabs.slice(MAX_VISIBLE_TABS); // Remaining tabs

  const activeExtraTab = extraTabs.find((tab) => tab.id === activeTab);
  const moreLabel = activeExtraTab ? activeExtraTab.label : "More";
  const moreCount = activeExtraTab?.count;

  const tabContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position and update button visibility
  const updateScrollState = () => {
    if (tabContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  useEffect(() => {
    updateScrollState();
    const handleResize = () => updateScrollState();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll functions
  const scrollTabs = (direction) => {
    if (!tabContainerRef.current) return;
    const scrollAmount = 150;
    tabContainerRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative w-full flex items-center">
      {/* Left Scroll Button */}
      {canScrollLeft && (
        <button
          onClick={() => scrollTabs("left")}
          className="absolute left-0 z-10 h-full px-2 rounded-l-xl bg-gradient-to-r from-background via-background/80 to-transparent 
          flex items-center justify-center text-secondary hover:text-secondary/80 transition"
        >
          <IoChevronBack className="w-5 h-5" />
        </button>
      )}

      {/* Tab Container */}
      <div
        ref={tabContainerRef}
        className="flex gap-2 p-[2px] bg-transparent border-[1.6px] border-secondary/50 rounded-xl overflow-x-auto no-scrollbar whitespace-nowrap scroll-smooth"
        onScroll={updateScrollState}
      >
        {/* Visible Tabs */}
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 pl-3 pr-1 py-1 text-caption rounded-[10px]  ${
              activeTab === tab.id
                ? "bg-secondary/90 text-background shadow-sm font-bold"
                : "text-secondary/60 hover:bg-secondary/10"
            }`}
          >
            <span className="text-sm font-medium">{tab.label}</span>
            {tab.count !== undefined && (
              <span
                className={`text-xs px-1.5 py-1 rounded-md ${
                  activeTab === tab.id
                    ? "bg-background/90 text-secondary/90"
                    : "bg-secondary/70 text-background"
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}

        {/* More Dropdown (Only if there are extra tabs) */}
        {extraTabs.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={`flex items-center gap-2 pl-3 pr-1 py-1 text-caption rounded-lg transition-colors ${
                  activeExtraTab
                    ? "bg-secondary/90 text-background shadow-sm font-bold"
                    : "text-secondary/60 hover:bg-secondary/10"
                }`}
              >
                <span className="text-sm font-medium">{moreLabel}</span>
                {moreCount !== undefined && (
                  <span className="text-xs px-1.5 py-1 bg-background/90 text-secondary/90 rounded-md">
                    {moreCount}
                  </span>
                )}
                <IoChevronDownOutline className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-1 w-full min-w-[150px] bg-background border border-secondary/60 rounded-lg shadow-lg">
              <div className="flex flex-col gap-1">
                {extraTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="px-3 py-2 flex items-center gap-2 text-secondary text-body2 hover:bg-secondary/20 transition rounded-md"
                  >
                    <span>{tab.label}</span>
                    {tab.count !== undefined && (
                      <span className="text-xs px-1.5 py-1 bg-secondary/70 text-background rounded-md">
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Right Scroll Button */}
      {canScrollRight && (
        <button
          onClick={() => scrollTabs("right")}
          className="absolute right-0 z-10 h-full px-2 rounded-r-xl bg-gradient-to-l from-background via-background/80 to-transparent 
          flex items-center justify-center text-secondary hover:text-secondary/80 transition"
        >
          <IoChevronForward className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default TabComponent;
